<script setup>
import { ref, computed, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import CrmPipelinesAPI from 'dashboard/api/crm/pipelines';
import CrmCustomFieldDefinitionsAPI from 'dashboard/api/crm/customFieldDefinitions';
import CrmDealOriginsAPI from 'dashboard/api/crm/dealOrigins';
import CrmLossReasonsAPI from 'dashboard/api/crm/lossReasons';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['import']);
const { t } = useI18n();

const uiFlags = useMapGetter('contacts/getUIFlags');
const isImportingContact = computed(() => uiFlags.value.isImporting);

const fileInput = ref(null);
const isOpen = ref(false);
const hasSelectedFile = ref(null);
const selectedFileName = ref('');
const csvHeaders = ref([]);

// Step management
const step = ref(1); // 1 = file, 2 = mapping

// CRM optional fields
const pipelines = ref([]);
const stages = ref([]);
const selectedPipelineId = ref(null);
const selectedStageId = ref(null);
const isLoadingMeta = ref(false);

// Phone +55 sanitization option
const sanitizePhoneBrazil = ref(false);

// Lookup data
const customFieldDefinitions = ref([]);
const dealOrigins = ref([]);
const lossReasons = ref([]);

// CSV Header → System field mapping
// Each CSV header maps to a system field key (contact.name, deal.amount, custom.api_key, etc.)
const headerMappings = ref({});

const CONTACT_FIELDS = [
  { key: 'contact.name', label: 'Nome', group: 'Contato' },
  { key: 'contact.email', label: 'E-mail', group: 'Contato' },
  { key: 'contact.phone_number', label: 'Telefone', group: 'Contato' },
  { key: 'contact.identifier', label: 'Identificador', group: 'Contato' },
  { key: 'contact.company', label: 'Empresa', group: 'Contato' },
  { key: 'contact.city', label: 'Cidade', group: 'Contato' },
];

const DEAL_FIELDS = [
  { key: 'deal.name', label: 'Nome da Oportunidade', group: 'Oportunidade' },
  { key: 'deal.amount', label: 'Valor (R$)', group: 'Oportunidade' },
  {
    key: 'deal.status',
    label: 'Status (open/won/lost)',
    group: 'Oportunidade',
  },
  { key: 'deal.created_at', label: 'Data de Criação', group: 'Oportunidade' },
  {
    key: 'deal.user',
    label: 'Responsável (nome ou e-mail)',
    group: 'Oportunidade',
  },
  { key: 'deal.origin', label: 'Origem (nome)', group: 'Oportunidade' },
  {
    key: 'deal.loss_reason',
    label: 'Motivo de Perda (nome)',
    group: 'Oportunidade',
  },
  { key: 'deal.rating', label: 'Avaliação (0-5)', group: 'Oportunidade' },
];

const systemFieldOptions = computed(() => {
  const options = [
    { key: '', label: '— Ignorar coluna —', group: '' },
    ...CONTACT_FIELDS,
    ...(selectedPipelineId.value ? DEAL_FIELDS : []),
    ...customFieldDefinitions.value.map(f => ({
      key: `custom.${f.api_key}`,
      label: `${f.name} (${f.field_type})`,
      group: 'Campo Personalizado',
    })),
  ];
  return options;
});

const csvUrl = '/downloads/import-contacts-sample.csv';

const handleFileClick = () => fileInput.value?.click();

const processFileName = fileName => {
  const lastDotIndex = fileName.lastIndexOf('.');
  const extension = fileName.slice(lastDotIndex);
  const baseName = fileName.slice(0, lastDotIndex);
  return baseName.length > 30
    ? `${baseName.slice(0, 30)}...${extension}`
    : fileName;
};

const readCsvHeaders = file => {
  const reader = new FileReader();
  reader.onload = e => {
    const text = e.target.result;
    const firstLine = text.split('\n')[0];
    if (firstLine) {
      csvHeaders.value = firstLine
        .split(',')
        .map(h => h.trim().replace(/^["']|["']$/g, ''))
        .filter(Boolean);

      // Auto-map known headers
      const autoMap = {
        name: 'contact.name',
        nome: 'contact.name',
        email: 'contact.email',
        phone_number: 'contact.phone_number',
        telefone: 'contact.phone_number',
        phone: 'contact.phone_number',
        identifier: 'contact.identifier',
        company: 'contact.company',
        empresa: 'contact.company',
        city: 'contact.city',
        cidade: 'contact.city',
        valor: 'deal.amount',
        amount: 'deal.amount',
        value: 'deal.amount',
        created_at: 'deal.created_at',
        data_criacao: 'deal.created_at',
      };

      const mappings = {};
      csvHeaders.value.forEach(h => {
        const normalized = h.toLowerCase().trim().replace(/\s+/g, '_');
        mappings[h] = autoMap[normalized] || '';
      });
      headerMappings.value = mappings;
    }
  };
  reader.readAsText(file);
};

const handleFileChange = () => {
  const file = fileInput.value?.files[0];
  hasSelectedFile.value = file;
  selectedFileName.value = file ? processFileName(file.name) : '';
  if (file) readCsvHeaders(file);
};

const handleRemoveFile = () => {
  hasSelectedFile.value = null;
  if (fileInput.value) {
    fileInput.value.value = null;
  }
  selectedFileName.value = '';
  csvHeaders.value = [];
  headerMappings.value = {};
  step.value = 1;
};

const fetchMeta = async () => {
  isLoadingMeta.value = true;
  try {
    const [pipelinesRes, fieldsRes, originsRes, lossRes] =
      await Promise.all([
        CrmPipelinesAPI.get(),
        CrmCustomFieldDefinitionsAPI.get(),
        CrmDealOriginsAPI.get().catch(() => ({ data: [] })),
        CrmLossReasonsAPI.get().catch(() => ({ data: [] })),
      ]);
    pipelines.value = pipelinesRes.data;
    customFieldDefinitions.value = fieldsRes.data;
    dealOrigins.value = originsRes.data;
    lossReasons.value = lossRes.data;

    // Auto-map custom fields by api_key
    customFieldDefinitions.value.forEach(f => {
      csvHeaders.value.forEach(h => {
        const normalized = h.toLowerCase().trim().replace(/\s+/g, '_');
        if (normalized === f.api_key && !headerMappings.value[h]) {
          headerMappings.value[h] = `custom.${f.api_key}`;
        }
      });
    });
  } catch {
    pipelines.value = [];
    customFieldDefinitions.value = [];
  }
  isLoadingMeta.value = false;
};

watch(selectedPipelineId, val => {
  selectedStageId.value = null;
  if (val) {
    const pipeline = pipelines.value.find(p => p.id === Number(val));
    stages.value = pipeline?.stages || [];
  } else {
    stages.value = [];
  }
});

const goToMapping = async () => {
  step.value = 2;
  await fetchMeta();
};

const mappedContactFields = computed(() => {
  return Object.values(headerMappings.value).filter(v =>
    v.startsWith('contact.')
  ).length;
});

const hasCrmMapping = computed(() => {
  return Object.values(headerMappings.value).some(
    v => v.startsWith('deal.') || v.startsWith('custom.')
  );
});

const canImport = computed(() => {
  if (!hasSelectedFile.value) return false;
  // Must have at least one contact field mapped
  if (mappedContactFields.value === 0) return false;
  // If CRM mapping exists, pipeline+stage must be selected
  if (
    hasCrmMapping.value &&
    (!selectedPipelineId.value || !selectedStageId.value)
  )
    return false;
  return true;
});

const uploadFile = () => {
  if (!hasSelectedFile.value || !canImport.value) return;

  // Build reverse mapping: system_key → csv_header
  const reverseMap = {};
  Object.entries(headerMappings.value).forEach(([csvHeader, sysKey]) => {
    if (sysKey) reverseMap[sysKey] = csvHeader;
  });

  // Contact field mappings: { system_field: csv_column }
  const contactMappings = {};
  Object.entries(reverseMap).forEach(([sysKey, csvHeader]) => {
    if (sysKey.startsWith('contact.')) {
      contactMappings[sysKey.replace('contact.', '')] = csvHeader;
    }
  });

  const crmParams = {};
  crmParams.contactFieldMappings = contactMappings;

  if (selectedPipelineId.value && selectedStageId.value) {
    crmParams.pipelineId = selectedPipelineId.value;
    crmParams.stageId = selectedStageId.value;

    // Deal field mappings: { field_name: csv_column }
    const dealMappings = {};
    Object.entries(reverseMap).forEach(([sysKey, csvHeader]) => {
      if (sysKey.startsWith('deal.')) {
        dealMappings[sysKey.replace('deal.', '')] = csvHeader;
      }
    });
    if (Object.keys(dealMappings).length)
      crmParams.dealFieldMappings = dealMappings;

    // Custom fields: { api_key: csv_column }
    const cfMappings = {};
    Object.entries(reverseMap).forEach(([sysKey, csvHeader]) => {
      if (sysKey.startsWith('custom.')) {
        cfMappings[sysKey.replace('custom.', '')] = csvHeader;
      }
    });
    if (Object.keys(cfMappings).length)
      crmParams.customFieldMappings = cfMappings;
  }

  if (sanitizePhoneBrazil.value) {
    crmParams.sanitizePhoneBrazil = true;
  }

  emit('import', { file: hasSelectedFile.value, crmParams });
};

// Dialog control
const open = () => {
  isOpen.value = true;
  step.value = 1;
  hasSelectedFile.value = null;
  selectedFileName.value = '';
  csvHeaders.value = [];
  headerMappings.value = {};
  selectedPipelineId.value = null;
  selectedStageId.value = null;
  sanitizePhoneBrazil.value = false;
};

const close = () => {
  isOpen.value = false;
};

const dialogRef = { open, close };

defineExpose({ dialogRef });
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-n-alpha-black1 backdrop-blur-[4px]"
    >
      <div
        class="relative w-full max-w-3xl max-h-[85vh] flex flex-col bg-n-alpha-3 backdrop-blur-[100px] shadow-xl rounded-2xl overflow-hidden mx-4"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak flex-shrink-0"
        >
          <div>
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.TITLE') }}
            </h3>
            <p class="mt-0.5 text-xs text-n-slate-9">
              {{
                step === 1
                  ? 'Selecione o arquivo CSV para iniciar'
                  : 'Mapeie as colunas do CSV para os campos do sistema'
              }}
            </p>
          </div>
          <button
            class="flex items-center justify-center rounded-lg size-8 hover:bg-n-alpha-2 transition-colors"
            @click="close"
          >
            <span class="i-lucide-x size-4 text-n-slate-10" />
          </button>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-y-auto px-6 py-5">
          <!-- Step 1: File picker -->
          <div v-if="step === 1" class="space-y-4">
            <p class="text-sm text-n-slate-11">
              {{
                t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.DESCRIPTION')
              }}
              <a
                :href="csvUrl"
                target="_blank"
                rel="noopener noreferrer"
                download="import-contacts-sample.csv"
                class="text-n-blue-11 ml-1"
              >
                {{
                  t(
                    'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.DOWNLOAD_LABEL'
                  )
                }}
              </a>
            </p>

            <div
              class="flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-xl transition-colors"
              :class="
                hasSelectedFile
                  ? 'border-woot-500 bg-woot-25 dark:bg-woot-900/10'
                  : 'border-n-weak hover:border-n-strong'
              "
            >
              <template v-if="!hasSelectedFile">
                <span
                  class="i-lucide-upload-cloud size-10 text-n-slate-8 mb-3"
                />
                <p class="text-sm text-n-slate-11 mb-2">
                  Arraste o arquivo ou clique para selecionar
                </p>
                <Button
                  :label="
                    t(
                      'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.CHOOSE_FILE'
                    )
                  "
                  icon="i-lucide-upload"
                  color="slate"
                  variant="faded"
                  size="sm"
                  @click="handleFileClick"
                />
              </template>
              <template v-else>
                <div class="flex items-center gap-3">
                  <span
                    class="i-lucide-file-spreadsheet size-8 text-woot-500"
                  />
                  <div>
                    <p class="text-sm font-medium text-n-slate-12">
                      {{ selectedFileName }}
                    </p>
                    <p class="text-xs text-n-slate-9">
                      {{ csvHeaders.length }} colunas detectadas
                    </p>
                  </div>
                  <Button
                    icon="i-lucide-trash-2"
                    color="ruby"
                    variant="ghost"
                    size="sm"
                    @click="handleRemoveFile"
                  />
                </div>
              </template>
            </div>
          </div>

          <!-- Step 2: Mapping -->
          <div v-if="step === 2" class="space-y-5">
            <!-- File summary bar -->
            <div
              class="flex items-center gap-2.5 px-3.5 py-2 rounded-lg bg-n-alpha-2 border border-n-weak"
            >
              <span class="i-lucide-file-spreadsheet size-4 text-woot-500" />
              <span class="text-xs font-medium text-n-slate-12 flex-1">
                {{ selectedFileName }}
              </span>
              <span class="text-[10px] text-n-slate-9">
                {{ csvHeaders.length }} colunas
              </span>
            </div>

            <!-- CRM Pipeline/Stage (optional) -->
            <div
              class="p-4 rounded-xl bg-woot-25 dark:bg-woot-900/10 border border-woot-200 dark:border-woot-800"
            >
              <h4
                class="text-xs font-semibold text-woot-600 dark:text-woot-400 mb-3 flex items-center gap-1.5"
              >
                <span class="i-lucide-briefcase size-3.5" />
                Criar oportunidades no CRM (opcional)
              </h4>
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label
                    class="text-[11px] font-medium text-n-slate-11 block mb-1"
                  >
                    Pipeline
                  </label>
                  <select
                    v-model="selectedPipelineId"
                    class="!mb-0 !bg-none w-full text-xs border border-n-weak rounded-lg px-2.5 py-1.5 bg-white dark:bg-n-solid-2 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
                  >
                    <option :value="null">Nenhum (só importar contatos)</option>
                    <option v-for="p in pipelines" :key="p.id" :value="p.id">
                      {{ p.name }}
                    </option>
                  </select>
                </div>
                <div>
                  <label
                    class="text-[11px] font-medium text-n-slate-11 block mb-1"
                  >
                    Etapa
                  </label>
                  <select
                    v-model="selectedStageId"
                    class="!mb-0 !bg-none w-full text-xs border border-n-weak rounded-lg px-2.5 py-1.5 bg-white dark:bg-n-solid-2 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
                    :disabled="!selectedPipelineId"
                  >
                    <option :value="null">Selecione...</option>
                    <option v-for="s in stages" :key="s.id" :value="s.id">
                      {{ s.name }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Loading -->
            <div
              v-if="isLoadingMeta"
              class="flex items-center justify-center py-6"
            >
              <span
                class="animate-spin i-lucide-loader-2 size-5 text-woot-500"
              />
            </div>

            <!-- Column mapping table -->
            <div v-else class="space-y-2">
              <div class="flex items-center justify-between">
                <h4
                  class="text-xs font-semibold text-n-slate-12 flex items-center gap-1.5"
                >
                  <span class="i-lucide-columns-3 size-3.5 text-n-slate-9" />
                  Mapeamento de Colunas
                </h4>
                <span class="text-[10px] text-n-slate-9">
                  {{ mappedContactFields }} campo(s) de contato mapeado(s)
                </span>
              </div>

              <div
                class="border border-n-weak rounded-xl overflow-hidden divide-y divide-n-weak"
              >
                <!-- Table header -->
                <div
                  class="grid grid-cols-[1fr,auto,1fr] items-center gap-3 px-4 py-2 bg-n-alpha-2"
                >
                  <span
                    class="text-[10px] font-semibold text-n-slate-10 uppercase tracking-wider"
                  >
                    Coluna do CSV
                  </span>
                  <span class="size-4" />
                  <span
                    class="text-[10px] font-semibold text-n-slate-10 uppercase tracking-wider"
                  >
                    Campo no Sistema
                  </span>
                </div>

                <!-- Rows -->
                <div
                  v-for="header in csvHeaders"
                  :key="header"
                  class="grid grid-cols-[1fr,auto,1fr] items-center gap-3 px-4 py-2 hover:bg-n-alpha-1 transition-colors"
                >
                  <div class="flex items-center gap-2 min-w-0">
                    <span
                      class="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-10 flex-shrink-0"
                    >
                      CSV
                    </span>
                    <span
                      class="text-xs text-n-slate-12 font-medium truncate"
                      :title="header"
                    >
                      {{ header }}
                    </span>
                  </div>

                  <span
                    class="i-lucide-arrow-right size-3.5 flex-shrink-0"
                    :class="
                      headerMappings[header]
                        ? 'text-woot-500'
                        : 'text-n-slate-7'
                    "
                  />

                  <select
                    v-model="headerMappings[header]"
                    class="!mb-0 !bg-none w-full text-xs border rounded-lg px-2.5 py-1.5 bg-white dark:bg-n-solid-2 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500 transition-colors"
                    :class="
                      headerMappings[header]
                        ? 'border-woot-300 dark:border-woot-700'
                        : 'border-n-weak'
                    "
                  >
                    <template
                      v-for="(opt, idx) in systemFieldOptions"
                      :key="`${opt.key}-${idx}`"
                    >
                      <option
                        v-if="
                          idx === 0 ||
                          systemFieldOptions[idx - 1].group !== opt.group
                        "
                        disabled
                        class="text-[10px] font-bold text-n-slate-9"
                      >
                        {{ opt.group ? `── ${opt.group} ──` : '' }}
                      </option>
                      <option :value="opt.key">
                        {{ opt.label }}
                      </option>
                    </template>
                  </select>
                </div>
              </div>

              <!-- Validation messages -->
              <div
                v-if="mappedContactFields === 0"
                class="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800"
              >
                <span
                  class="i-lucide-alert-triangle size-3.5 text-amber-600 flex-shrink-0"
                />
                <span class="text-[11px] text-amber-700 dark:text-amber-400">
                  Mapeie pelo menos um campo de contato (nome, e-mail ou
                  telefone) para continuar.
                </span>
              </div>
              <div
                v-else-if="
                  hasCrmMapping && (!selectedPipelineId || !selectedStageId)
                "
                class="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800"
              >
                <span
                  class="i-lucide-alert-triangle size-3.5 text-amber-600 flex-shrink-0"
                />
                <span class="text-[11px] text-amber-700 dark:text-amber-400">
                  Selecione um Pipeline e Etapa para mapear campos de
                  oportunidade.
                </span>
              </div>
              <div
                v-else-if="canImport"
                class="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
              >
                <span
                  class="i-lucide-check-circle-2 size-3.5 text-green-600 flex-shrink-0"
                />
                <span class="text-[11px] text-green-700 dark:text-green-400">
                  {{
                    selectedPipelineId
                      ? 'Contatos e oportunidades serão criados ao clicar em Importar.'
                      : 'Contatos serão importados ao clicar em Importar.'
                  }}
                </span>
              </div>

              <!-- Phone +55 sanitization option -->
              <div
                class="flex items-start gap-3 p-3.5 rounded-xl bg-n-alpha-2 border border-n-weak"
              >
                <input
                  id="sanitize-phone-brazil"
                  v-model="sanitizePhoneBrazil"
                  type="checkbox"
                  class="mt-0.5 size-4 rounded border-n-weak text-woot-500 focus:ring-woot-500 cursor-pointer"
                />
                <label
                  for="sanitize-phone-brazil"
                  class="cursor-pointer select-none"
                >
                  <span
                    class="text-xs font-medium text-n-slate-12 flex items-center gap-1.5"
                  >
                    <span class="i-lucide-phone size-3.5 text-n-slate-9" />
                    Adicionar +55 nos telefones (Brasil)
                  </span>
                  <span class="block text-[11px] text-n-slate-9 mt-0.5">
                    Remove caracteres especiais do telefone e adiciona o
                    código do país +55 caso não esteja presente. Números que
                    já possuem outro DDI serão mantidos.
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div
          class="flex items-center justify-between px-6 py-4 border-t border-n-weak flex-shrink-0"
        >
          <div>
            <span
              v-if="step === 2"
              class="text-[10px] text-n-slate-8 cursor-pointer hover:text-n-slate-11 transition-colors flex items-center gap-1"
              @click="step = 1"
            >
              <span class="i-lucide-arrow-left size-3" />
              Voltar
            </span>
          </div>
          <div class="flex items-center gap-2">
            <Button
              variant="faded"
              color="slate"
              :label="t('DIALOG.BUTTONS.CANCEL')"
              @click="close"
            />
            <Button
              v-if="step === 1"
              color="blue"
              label="Continuar"
              icon="i-lucide-arrow-right"
              :disabled="!hasSelectedFile"
              @click="goToMapping"
            />
            <Button
              v-else
              color="blue"
              :label="t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.IMPORT')"
              icon="i-lucide-upload"
              :is-loading="isImportingContact"
              :disabled="!canImport || isImportingContact"
              @click="uploadFile"
            />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
  <input
    ref="fileInput"
    type="file"
    accept="text/csv"
    class="hidden"
    @change="handleFileChange"
  />
</template>
